import { useState } from "react";
import { Link } from "wouter";
import { RequireAdmin, useAuth } from "../lib/auth";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { ImageUpload } from "../components/ImageUpload";

const AREAS = [
  "渋谷", "新宿", "池袋", "銀座", "表参道", "恵比寿", "中目黒", "自由が丘", "吉祥寺", "二子玉川", "その他",
] as const;

type Area = typeof AREAS[number];

interface StoreFormData {
  name: string;
  description: string;
  area: Area;
  address: string;
  nearestStation: string;
  walkingMinutes: number;
  strollerEntry: boolean;
  hasElevator: boolean;
  hasDiaperChanging: boolean;
  hasNursingRoom: boolean;
  hasKidsMenu: boolean;
  hasHighChair: boolean;
  hasSofaSeating: boolean;
  babyFoodAllowed: boolean;
  phoneNumber: string;
  website: string;
  openingHours: string;
  thumbnailUrl: string;
}

const defaultFormData: StoreFormData = {
  name: "",
  description: "",
  area: "渋谷",
  address: "",
  nearestStation: "",
  walkingMinutes: 5,
  strollerEntry: true,
  hasElevator: false,
  hasDiaperChanging: false,
  hasNursingRoom: false,
  hasKidsMenu: false,
  hasHighChair: false,
  hasSofaSeating: false,
  babyFoodAllowed: false,
  phoneNumber: "",
  website: "",
  openingHours: "",
  thumbnailUrl: "",
};

export default function AdminPage() {
  const { isAuthenticated, loginDemo } = useAuth();

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center">
                <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <circle cx="7" cy="20" r="2" />
                  <circle cx="17" cy="20" r="2" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 18H7V9a4 4 0 014-4h2a4 4 0 014 4v3" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 9h14" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l2-4" />
                </svg>
              </div>
              <span className="text-xl font-bold text-stone-900">SONOMAMA</span>
            </div>
            <p className="text-sm text-stone-500 mt-1">管理画面</p>
          </div>

          <div className="rounded-2xl bg-white p-8 shadow-lg">
            <h1 className="text-xl font-bold text-stone-900 text-center">ログイン</h1>
            <p className="mt-2 text-sm text-stone-500 text-center">
              管理画面にアクセスするにはログインが必要です
            </p>
            <div className="mt-6 space-y-3">
              <button
                onClick={loginDemo}
                className="w-full flex items-center justify-center gap-3 rounded-lg border border-stone-300 bg-white px-4 py-3 text-sm font-medium text-stone-700 shadow-sm hover:bg-stone-50 transition-colors"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Googleでログイン
              </button>
              <button
                onClick={loginDemo}
                className="w-full flex items-center justify-center gap-3 rounded-lg border border-stone-300 bg-white px-4 py-3 text-sm font-medium text-stone-700 shadow-sm hover:bg-stone-50 transition-colors"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#F25022" d="M1 1h10v10H1z"/>
                  <path fill="#00A4EF" d="M1 13h10v10H1z"/>
                  <path fill="#7FBA00" d="M13 1h10v10H13z"/>
                  <path fill="#FFB900" d="M13 13h10v10H13z"/>
                </svg>
                Microsoftでログイン
              </button>
            </div>
            <p className="mt-4 text-xs text-stone-400 text-center">
              ※ デモ用のため、実際の認証は行われません
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <RequireAdmin>
      <AdminDashboard />
    </RequireAdmin>
  );
}

function AdminDashboard() {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState<"stores" | "articles" | "tokens">("stores");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-stone-100">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-stone-900 text-white px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 rounded-lg hover:bg-stone-800"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center">
            <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <circle cx="7" cy="20" r="2" />
              <circle cx="17" cy="20" r="2" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 18H7V9a4 4 0 014-4h2a4 4 0 014 4v3" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 9h14" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l2-4" />
            </svg>
          </div>
          <span className="font-bold text-sm">SONOMAMA 管理</span>
        </div>
        <div className="w-10" /> {/* Spacer */}
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 bg-black/50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Admin Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 w-64 bg-stone-900 text-white z-50
        transform transition-transform duration-300 lg:translate-x-0
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
      `}>
        {/* Logo */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-stone-700">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center flex-shrink-0">
              <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <circle cx="7" cy="20" r="2" />
                <circle cx="17" cy="20" r="2" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 18H7V9a4 4 0 014-4h2a4 4 0 014 4v3" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 9h14" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l2-4" />
              </svg>
            </div>
            <div>
              <div className="font-bold text-sm">SONOMAMA</div>
              <div className="text-xs text-stone-400">管理画面</div>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-lg hover:bg-stone-800"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          <SidebarButton
            active={activeTab === "stores"}
            onClick={() => { setActiveTab("stores"); setSidebarOpen(false); }}
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            }
          >
            店舗管理
          </SidebarButton>
          <SidebarButton
            active={activeTab === "articles"}
            onClick={() => { setActiveTab("articles"); setSidebarOpen(false); }}
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
              </svg>
            }
          >
            記事管理
          </SidebarButton>
          <SidebarButton
            active={activeTab === "tokens"}
            onClick={() => { setActiveTab("tokens"); setSidebarOpen(false); }}
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
            }
          >
            トークン管理
          </SidebarButton>

          <div className="border-t border-stone-700 mt-4 pt-4">
            <Link href="/">
              <button className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-stone-400 hover:bg-stone-800 hover:text-white transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                サイトに戻る
              </button>
            </Link>
          </div>
        </nav>

        {/* User Info */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-stone-700">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-stone-700 flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-medium">{user?.name?.charAt(0) || "U"}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-stone-400">{user?.role}</div>
            </div>
            <button
              onClick={logout}
              className="p-2 rounded-lg hover:bg-stone-800 transition-colors"
              title="ログアウト"
            >
              <svg className="w-5 h-5 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="lg:pl-64 pt-14 lg:pt-0">
        <div className="p-4 lg:p-8">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-stone-900">
              {activeTab === "stores" && "店舗管理"}
              {activeTab === "articles" && "記事管理"}
              {activeTab === "tokens" && "トークン管理"}
            </h1>
          </div>

          {activeTab === "stores" && <StoresAdmin />}
          {activeTab === "articles" && <ArticlesAdmin />}
          {activeTab === "tokens" && <TokensAdmin />}
        </div>
      </div>
    </div>
  );
}

function SidebarButton({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
        active
          ? "bg-teal-600 text-white"
          : "text-stone-300 hover:bg-stone-800 hover:text-white"
      }`}
    >
      {icon}
      {children}
    </button>
  );
}


function StoresAdmin() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedArea, setSelectedArea] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState<any>(null);
  const [formData, setFormData] = useState<StoreFormData>(defaultFormData);

  const utils = trpc.useUtils();

  const { data, isLoading } = trpc.stores.list.useQuery({
    page,
    limit: 20,
    search: searchQuery || undefined,
    area: selectedArea !== "all" ? (selectedArea as Area) : undefined,
  });

  const createMutation = trpc.stores.create.useMutation({
    onSuccess: () => {
      utils.stores.list.invalidate();
      setIsCreateDialogOpen(false);
      setFormData(defaultFormData);
    },
  });

  const updateMutation = trpc.stores.update.useMutation({
    onSuccess: () => {
      utils.stores.list.invalidate();
      setIsEditDialogOpen(false);
      setSelectedStore(null);
    },
  });

  const deleteMutation = trpc.stores.delete.useMutation({
    onSuccess: () => {
      utils.stores.list.invalidate();
      setIsDeleteDialogOpen(false);
      setSelectedStore(null);
    },
  });

  const handleCreate = () => {
    createMutation.mutate(formData);
  };

  const handleUpdate = () => {
    if (!selectedStore) return;
    updateMutation.mutate({
      id: selectedStore.id,
      data: formData,
    });
  };

  const handleDelete = () => {
    if (!selectedStore) return;
    deleteMutation.mutate({ id: selectedStore.id });
  };

  const openEditDialog = (store: any) => {
    setSelectedStore(store);
    setFormData({
      name: store.name || "",
      description: store.description || "",
      area: store.area || "渋谷",
      address: store.address || "",
      nearestStation: store.nearestStation || "",
      walkingMinutes: store.walkingMinutes || 5,
      strollerEntry: store.strollerEntry || false,
      hasElevator: store.hasElevator || false,
      hasDiaperChanging: store.hasDiaperChanging || false,
      hasNursingRoom: store.hasNursingRoom || false,
      hasKidsMenu: store.hasKidsMenu || false,
      hasHighChair: store.hasHighChair || false,
      hasSofaSeating: store.hasSofaSeating || false,
      babyFoodAllowed: store.babyFoodAllowed || false,
      phoneNumber: store.phoneNumber || "",
      website: store.website || "",
      openingHours: store.openingHours || "",
      thumbnailUrl: store.thumbnailUrl || "",
    });
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (store: any) => {
    setSelectedStore(store);
    setIsDeleteDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
            <Input
              placeholder="店舗名で検索..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={selectedArea} onValueChange={setSelectedArea}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="エリア" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">すべて</SelectItem>
              {AREAS.map((area) => (
                <SelectItem key={area} value={area}>
                  {area}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => {
          setFormData(defaultFormData);
          setIsCreateDialogOpen(true);
        }}>
          <Plus className="mr-2 h-4 w-4" />
          新規作成
        </Button>
      </div>

      <div className="rounded-lg border border-stone-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]">ID</TableHead>
              <TableHead>店舗名</TableHead>
              <TableHead>エリア</TableHead>
              <TableHead>最寄駅</TableHead>
              <TableHead>設備</TableHead>
              <TableHead className="w-[100px]">操作</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-stone-500">
                  読み込み中...
                </TableCell>
              </TableRow>
            ) : data?.items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-stone-500">
                  店舗が見つかりません
                </TableCell>
              </TableRow>
            ) : (
              data?.items.map((store) => (
                <TableRow key={store.id}>
                  <TableCell className="font-mono text-sm">{store.id}</TableCell>
                  <TableCell className="font-medium">{store.name}</TableCell>
                  <TableCell>
                    <Badge variant="secondary">{store.area}</Badge>
                  </TableCell>
                  <TableCell className="text-stone-600">
                    {store.nearestStation ? `${store.nearestStation}駅 徒歩${store.walkingMinutes}分` : "-"}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {store.strollerEntry && <Badge variant="success">ベビーカー</Badge>}
                      {store.hasDiaperChanging && <Badge variant="default">おむつ台</Badge>}
                      {store.hasKidsMenu && <Badge variant="warning">キッズメニュー</Badge>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(store)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDeleteDialog(store)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-stone-500">
            {data.total}件中 {(page - 1) * 20 + 1}-{Math.min(page * 20, data.total)}件を表示
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
            >
              前へ
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page === data.totalPages}
            >
              次へ
            </Button>
          </div>
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>新規店舗登録</DialogTitle>
            <DialogDescription>
              ベビーカーで入れるカフェ・レストランを登録します。
            </DialogDescription>
          </DialogHeader>
          <StoreForm formData={formData} setFormData={setFormData} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              キャンセル
            </Button>
            <Button onClick={handleCreate} disabled={createMutation.isPending}>
              {createMutation.isPending ? "登録中..." : "登録する"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>店舗編集</DialogTitle>
            <DialogDescription>
              店舗情報を編集します。
            </DialogDescription>
          </DialogHeader>
          <StoreForm formData={formData} setFormData={setFormData} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              キャンセル
            </Button>
            <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? "更新中..." : "更新する"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>店舗を削除</DialogTitle>
            <DialogDescription>
              「{selectedStore?.name}」を削除しますか？この操作は取り消せません。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              キャンセル
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? "削除中..." : "削除する"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StoreForm({
  formData,
  setFormData,
}: {
  formData: StoreFormData;
  setFormData: React.Dispatch<React.SetStateAction<StoreFormData>>;
}) {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="name">店舗名 *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="例: スターバックス 渋谷店"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="area">エリア *</Label>
          <Select
            value={formData.area}
            onValueChange={(value) => setFormData({ ...formData, area: value as Area })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AREAS.map((area) => (
                <SelectItem key={area} value={area}>
                  {area}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="address">住所 *</Label>
        <Input
          id="address"
          value={formData.address}
          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
          placeholder="例: 東京都渋谷区道玄坂1-2-3"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">説明</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="ベビーカーでの入店のしやすさ、店内の様子などを記載"
          rows={3}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="nearestStation">最寄駅</Label>
          <Input
            id="nearestStation"
            value={formData.nearestStation}
            onChange={(e) => setFormData({ ...formData, nearestStation: e.target.value })}
            placeholder="例: 渋谷"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="walkingMinutes">徒歩（分）</Label>
          <Input
            id="walkingMinutes"
            type="number"
            min={1}
            max={60}
            value={formData.walkingMinutes}
            onChange={(e) => setFormData({ ...formData, walkingMinutes: parseInt(e.target.value) || 5 })}
          />
        </div>
      </div>

      <div className="space-y-4">
        <Label>設備情報</Label>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex items-center justify-between rounded-lg border border-stone-200 p-3">
            <Label htmlFor="strollerEntry" className="cursor-pointer">ベビーカー入店可</Label>
            <Switch
              id="strollerEntry"
              checked={formData.strollerEntry}
              onCheckedChange={(checked) => setFormData({ ...formData, strollerEntry: checked })}
            />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-stone-200 p-3">
            <Label htmlFor="hasElevator" className="cursor-pointer">エレベーターあり</Label>
            <Switch
              id="hasElevator"
              checked={formData.hasElevator}
              onCheckedChange={(checked) => setFormData({ ...formData, hasElevator: checked })}
            />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-stone-200 p-3">
            <Label htmlFor="hasDiaperChanging" className="cursor-pointer">おむつ交換台あり</Label>
            <Switch
              id="hasDiaperChanging"
              checked={formData.hasDiaperChanging}
              onCheckedChange={(checked) => setFormData({ ...formData, hasDiaperChanging: checked })}
            />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-stone-200 p-3">
            <Label htmlFor="hasNursingRoom" className="cursor-pointer">授乳室あり</Label>
            <Switch
              id="hasNursingRoom"
              checked={formData.hasNursingRoom}
              onCheckedChange={(checked) => setFormData({ ...formData, hasNursingRoom: checked })}
            />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-stone-200 p-3">
            <Label htmlFor="hasKidsMenu" className="cursor-pointer">キッズメニューあり</Label>
            <Switch
              id="hasKidsMenu"
              checked={formData.hasKidsMenu}
              onCheckedChange={(checked) => setFormData({ ...formData, hasKidsMenu: checked })}
            />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-stone-200 p-3">
            <Label htmlFor="hasHighChair" className="cursor-pointer">ハイチェアあり</Label>
            <Switch
              id="hasHighChair"
              checked={formData.hasHighChair}
              onCheckedChange={(checked) => setFormData({ ...formData, hasHighChair: checked })}
            />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-stone-200 p-3">
            <Label htmlFor="hasSofaSeating" className="cursor-pointer">ソファ席あり</Label>
            <Switch
              id="hasSofaSeating"
              checked={formData.hasSofaSeating}
              onCheckedChange={(checked) => setFormData({ ...formData, hasSofaSeating: checked })}
            />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-stone-200 p-3">
            <Label htmlFor="babyFoodAllowed" className="cursor-pointer">離乳食持込可</Label>
            <Switch
              id="babyFoodAllowed"
              checked={formData.babyFoodAllowed}
              onCheckedChange={(checked) => setFormData({ ...formData, babyFoodAllowed: checked })}
            />
          </div>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="phoneNumber">電話番号</Label>
          <Input
            id="phoneNumber"
            value={formData.phoneNumber}
            onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
            placeholder="例: 03-1234-5678"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="website">ウェブサイト</Label>
          <Input
            id="website"
            value={formData.website}
            onChange={(e) => setFormData({ ...formData, website: e.target.value })}
            placeholder="https://..."
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="openingHours">営業時間</Label>
        <Input
          id="openingHours"
          value={formData.openingHours}
          onChange={(e) => setFormData({ ...formData, openingHours: e.target.value })}
          placeholder="例: 10:00-20:00（月曜定休）"
        />
      </div>

      <div className="space-y-2">
        <Label>サムネイル画像</Label>
        <ImageUpload
          value={formData.thumbnailUrl}
          onChange={(url) => setFormData({ ...formData, thumbnailUrl: url })}
          onRemove={() => setFormData({ ...formData, thumbnailUrl: "" })}
          prefix="stores"
        />
        <p className="text-xs text-stone-500">
          JPEG, PNG, GIF, WebP（最大5MB）
        </p>
      </div>
    </div>
  );
}

function ArticlesAdmin() {
  return (
    <div className="rounded-lg border border-stone-200 bg-white p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-stone-900">記事一覧</h2>
        <Button>
          <Plus className="mr-2 h-4 w-4" />
          新規作成
        </Button>
      </div>
      <p className="mt-4 text-stone-500">
        記事管理機能は今後実装予定です。
      </p>
    </div>
  );
}

function TokensAdmin() {
  return (
    <div className="rounded-lg border border-stone-200 bg-white p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-stone-900">トークン一覧</h2>
      </div>
      <p className="mt-4 text-stone-500">
        トークン管理機能は今後実装予定です。
        <br />
        新しいトークンはCLIで generate-admin-token.ts を実行して生成します。
      </p>
    </div>
  );
}
